import { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import alffyLogo from "../assets/alffy-logo.png";
import MarketMarquee from "../components/MarketMarquee";
import QuoteCard from "../components/mobile/QuoteCard";
import StickyStockSearch from "../components/mobile/StickyStockSearch";
import LatestEarnings from "../components/mobile/LatestEarnings";
import ConceptChart from "../components/charts/ConceptChart";
import PriceHistoryChart from "../components/charts/PriceHistoryChart";
import CagrComparisonChart from "../components/charts/CagrComparisonChart";
import CagrGroupedBar from "../components/charts/CagrGroupedBar";
import RatioTrendChart from "../components/charts/RatioTrendChart";

// Default to the host the page was loaded from so the same bundle works when
// the phone hits the Mac via Tailscale / LAN IP — not just localhost.
const API_URL = process.env.REACT_APP_API_URL
  || (process.env.NODE_ENV === "development" ? `http://${window.location.hostname}:8000` : "");

// Default watchlist shown to every visitor (logged in or not, for now).
// When watchlist support lands for logged-in users, swap this for their saved list.
const DEFAULT_TICKERS = ["AAPL", "NVDA", "MSFT", "TSLA", "GOOGL", "AMZN", "META"];
const DEFAULT_TICKER = "AAPL";

// Reuse the same Google OAuth flow as the desktop LandingV2. After Google
// finishes, the redirect goes to /login which exchanges the id_token for a JWT.
// We set a localStorage key here so /login knows to come back to /mobile
// instead of the default /stockview.
function startGoogleAuthFromMobile() {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
  if (!clientId) {
    window.location.href = "/login";
    return;
  }
  localStorage.setItem("postLoginRedirect", "/mobile");
  const redirectUri = encodeURIComponent(window.location.origin + "/login");
  const nonce = Math.random().toString(36).substring(2, 18);
  window.location.href =
    `https://accounts.google.com/o/oauth2/v2/auth` +
    `?client_id=${clientId}` +
    `&redirect_uri=${redirectUri}` +
    `&response_type=id_token` +
    `&scope=openid%20email%20profile` +
    `&nonce=${nonce}` +
    `&prompt=select_account`;
}

// Hook: returns the list of tickers to show in the quote-card grid.
// Unauth users get the hardcoded default set; logged-in users will later get
// their watchlist tickers (placeholder for now — read token, return defaults).
function useDefaultTickers() {
  // TODO: when localStorage 'token' is present, fetch /user/watchlist and
  // return those tickers instead of the hardcoded defaults.
  return DEFAULT_TICKERS;
}

function formatCurrencyMillions(value) {
  if (value == null || isNaN(Number(value))) return 'N/A';
  return `$${(Number(value) / 1_000_000).toLocaleString('en-US', { maximumFractionDigits: 0 })}M`;
}

function formatNumber(value, suffix = '') {
  if (value == null || isNaN(Number(value))) return 'N/A';
  return `${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${suffix}`;
}

function formatCurrency(value, suffix = '') {
  if (value == null || isNaN(Number(value))) return 'N/A';
  return `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}${suffix}`;
}

function formatPct(value) {
  if (value == null || isNaN(Number(value))) return 'N/A';
  return `${Number(value).toFixed(2)}%`;
}

// Section card wrapper for consistent spacing/look
// Background matches CagrComparisonChart's panel so all tiles share the look.
function Card({ title, action, children }) {
  return (
    <section className="bg-gray-900/60 border border-gray-700/50 rounded-lg p-4">
      {(title || action) && (
        <div className="flex items-center justify-between mb-3">
          {title && <h3 className="text-sm font-semibold text-white tracking-wide">{title}</h3>}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

function MetricItem({ label, value, valueClass = 'text-white' }) {
  return (
    <div>
      <div className="text-gray-500 text-[10px] uppercase tracking-wide">{label}</div>
      <div className={`${valueClass} font-semibold text-sm mt-0.5`}>{value}</div>
    </div>
  );
}

export default function Mobile() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tickerParam = searchParams.get('ticker') || DEFAULT_TICKER;

  const tickers = useDefaultTickers();

  // Loading indicator removed for now (was a blue stripe at the top of the
  // header). Kept bumpInflight as a no-op stub so the existing fetch sites
  // don't need to be re-instrumented when we wire a new loader in prod.
  const bumpInflight = () => {};

  // ── Intro video: when it finishes, replay and freeze on the bull frame
  // (~1s in) instead of holding on the dark end frame.
  const introVideoRef = useRef(null);
  const introPauseTimeoutRef = useRef(null);
  const handleIntroVideoEnded = () => {
    const v = introVideoRef.current;
    if (!v) return;
    v.currentTime = 0;
    const p = v.play();
    // Swallow the AbortError that can fire if React tears down mid-play.
    if (p && typeof p.catch === "function") p.catch(() => {});
    if (introPauseTimeoutRef.current) clearTimeout(introPauseTimeoutRef.current);
    introPauseTimeoutRef.current = setTimeout(() => {
      if (introVideoRef.current) introVideoRef.current.pause();
    }, 1000);
  };
  useEffect(() => () => {
    if (introPauseTimeoutRef.current) clearTimeout(introPauseTimeoutRef.current);
  }, []);

  // ── Intro video time-keyed captions ──────────────────────────────────────
  // Shown right under the video so it feels like part of the clip. The 0-2s
  // window holds the closing tagline so it's already visible at startup AND
  // sticks during the on-end freeze-at-1s. Rest of the schedule is shifted
  // +2s from the original to make room for that intro caption.
  const INTRO_OVERLAYS = [
    { start:  0, end:  2, text: "Deep dive with us — no cards, no strings!" },
    { start:  2, end:  5, text: "Unleash your inner Buffett!" },
    { start:  7, end: 12, text: "No noise. Just the fundamentals." },
    { start: 14, end: 21, text: "Visualize everything." },
    { start: 24, end: 31, text: "Build your edge with deep fundamentals." },
    { start: 34, end: 42, text: "Deep dive into valuation." },
    { start: 43, end: 45, text: "Listen to intelligent summaries." },
    { start: 46, end: 52, text: "Deep dive with us — no cards, no strings!" },
  ];
  const [introVideoTime, setIntroVideoTime] = useState(0);
  const currentIntroOverlay = INTRO_OVERLAYS.find(
    o => introVideoTime >= o.start && introVideoTime <= o.end
  );

  // ── Quote card data ──────────────────────────────────────────────────────
  const [quotes, setQuotes] = useState([]);
  useEffect(() => {
    fetch(`${API_URL}/quote-cards?tickers=${tickers.join(',')}`)
      .then(r => r.json())
      .then(d => setQuotes(d.quotes || []))
      .catch(() => setQuotes([]));
  }, [tickers]);

  // ── Selected company (full record with cik) ──────────────────────────────
  const [selectedCompany, setSelectedCompany] = useState(null);
  useEffect(() => {
    if (!tickerParam) return;
    bumpInflight(1);
    fetch(`${API_URL}/search-tickers?q=${encodeURIComponent(tickerParam)}`)
      .then(r => r.json())
      .then(d => {
        const exact = (d.companies || []).find(c => c.ticker?.toUpperCase() === tickerParam.toUpperCase());
        setSelectedCompany(exact || (d.companies || [])[0] || null);
      })
      .catch(() => setSelectedCompany(null))
      .finally(() => bumpInflight(-1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickerParam]);

  const cik = selectedCompany?.cik;
  const ticker = selectedCompany?.ticker || tickerParam;

  const handleSelect = (company) => {
    if (!company?.ticker) return;
    setSelectedCompany(company);
    setSearchParams({ ticker: company.ticker });
  };

  const handleCardSelect = (t) => {
    setSearchParams({ ticker: t });
  };

  // ── Price history with timeframe selector ────────────────────────────────
  const [timeRange, setTimeRange] = useState('1D');
  const [priceHistory, setPriceHistory] = useState([]);
  useEffect(() => {
    if (!ticker) return;
    bumpInflight(1);
    fetch(`${API_URL}/stock-price-history?ticker=${encodeURIComponent(ticker)}&time_range=${timeRange}`)
      .then(r => r.json())
      .then(d => setPriceHistory(d.data || []))
      .catch(() => setPriceHistory([]))
      .finally(() => bumpInflight(-1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker, timeRange]);

  // ── Company metrics ──────────────────────────────────────────────────────
  const [companyMetrics, setCompanyMetrics] = useState(null);
  useEffect(() => {
    if (!ticker) return;
    bumpInflight(1);
    fetch(`${API_URL}/company-metrics?ticker=${encodeURIComponent(ticker)}`)
      .then(r => r.json())
      .then(d => setCompanyMetrics(d.metrics || null))
      .catch(() => setCompanyMetrics(null))
      .finally(() => bumpInflight(-1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker]);

  // ── Financial health (from /ttm-calculated-values) ───────────────────────
  // Note: valuation history (P/E, P/B timeseries) is part of /company-growth-metrics,
  // not /ttm-calculated-values. See growthMetrics.valuation_history below.
  const [financialHealth, setFinancialHealth] = useState(null);
  useEffect(() => {
    if (!cik) return;
    bumpInflight(1);
    fetch(`${API_URL}/ttm-calculated-values?cik=${cik}`)
      .then(r => r.json())
      .then(d => setFinancialHealth(d.financial_health || null))
      .catch(() => setFinancialHealth(null))
      .finally(() => bumpInflight(-1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cik]);

  // ── Income statement (quarterly) + SIC-aware Sankey map ──────────────────
  const [incomeStatement, setIncomeStatement] = useState([]);
  const [sankeyMap, setSankeyMap] = useState([]);
  useEffect(() => {
    if (!cik) return;
    bumpInflight(1);
    fetch(`${API_URL}/income-statement?cik=${cik}&is_quarterly=true`)
      .then(r => r.json())
      .then(d => setIncomeStatement(d.data || []))
      .catch(() => setIncomeStatement([]))
      .finally(() => bumpInflight(-1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cik]);

  // Pick the sankey-map flavor based on SIC code from the first IS row.
  // SIC 6020-6212 = depository/savings/credit (banks), 6311-6411 = insurance.
  // Anything else (or unknown) → standard. Matches desktop StockView logic.
  const sankeyType = useMemo(() => {
    if (!incomeStatement || incomeStatement.length === 0) return null;
    const sic = parseInt(incomeStatement[0].sic);
    if (!isNaN(sic)) {
      if (sic >= 6020 && sic <= 6212) return 'bank';
      if (sic >= 6311 && sic <= 6411) return 'insurance';
    }
    return 'standard';
  }, [incomeStatement]);

  useEffect(() => {
    if (!sankeyType) { setSankeyMap([]); return; }
    bumpInflight(1);
    fetch(`${API_URL}/sankey-map?type=${sankeyType}`)
      .then(r => r.json())
      .then(d => setSankeyMap(d.data || []))
      .catch(() => setSankeyMap([]))
      .finally(() => bumpInflight(-1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sankeyType]);

  // ── Growth metrics for CAGRs (and P/E + P/B valuation history) ──────────
  const [growthMetrics, setGrowthMetrics] = useState(null);
  useEffect(() => {
    if (!ticker) return;
    setGrowthMetrics(null); // clear stale
    bumpInflight(1);
    fetch(`${API_URL}/company-growth-metrics?ticker=${encodeURIComponent(ticker)}`)
      .then(r => r.json())
      .then(d => setGrowthMetrics(d.metrics || null))
      .catch(() => setGrowthMetrics(null))
      .finally(() => bumpInflight(-1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker]);

  // ── P/E and P/B range selectors ──────────────────────────────────────────
  const [peRange, setPeRange] = useState('3Y');
  const [pbRange, setPbRange] = useState('3Y');

  // ── CAGR series — shared shape used by all three CAGR visualizations ────
  // Stock Price's 4th period is 1Y (the API doesn't expose a TTM price CAGR),
  // shown alongside the other two metrics' TTM values in the same column.
  const cagrPeriods = ['10Y', '5Y', '3Y', 'TTM'];
  const cagrCompareSeries = useMemo(() => {
    const g = growthMetrics || {};
    return [
      { name: 'Revenue',     color: '#22c55e', values: [g.revenue_cagr?.['10y'], g.revenue_cagr?.['5y'], g.revenue_cagr?.['3y'], g.revenue_cagr?.ttm] },
      { name: 'Net Profit',  color: '#3b82f6', values: [g.net_profit_cagr?.['10y'], g.net_profit_cagr?.['5y'], g.net_profit_cagr?.['3y'], g.net_profit_cagr?.ttm] },
      { name: 'Stock Price', color: '#f59e0b', values: [g.price_cagr?.['10y'], g.price_cagr?.['5y'], g.price_cagr?.['3y'], g.price_cagr?.['1y']] },
    ];
  }, [growthMetrics]);

  const valuationTs = growthMetrics?.valuation_history;

  const isLoggedIn = typeof window !== 'undefined' && !!localStorage.getItem('token') && localStorage.getItem('isGuest') !== 'true';
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleLogout = () => {
    localStorage.setItem('token', 'guest_token');
    localStorage.setItem('userEmail', 'guest@alffy.com');
    localStorage.setItem('userName', 'Guest');
    localStorage.setItem('isGuest', 'true');
    localStorage.removeItem('userPicture');
    setIsMenuOpen(false);
    // Reload so isLoggedIn state refreshes and stays on /mobile
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Sticky top bar: logo + wordmark + auth buttons.
          Ticker tape (MarketMarquee) is intentionally hidden for now — keeping
          the import so it can be re-added in a future iteration. */}
      <header className="sticky top-0 z-50 bg-black/95 backdrop-blur-md border-b border-gray-800">
        <div className="flex items-center justify-between px-3 py-2">
          {/* Logo stays inside /mobile so we don't bounce users to the desktop
              landing page (which forces landscape on phones). */}
          <Link to="/mobile" className="flex items-center gap-2 shrink-0">
            <img src={alffyLogo} alt="" className="h-7 w-7" />
            <span className="text-white font-bold text-lg tracking-tight">alffy</span>
          </Link>

          {!isLoggedIn ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={startGoogleAuthFromMobile}
                className="text-gray-200 text-xs font-medium px-2.5 py-1.5"
              >
                Login
              </button>
              <button
                type="button"
                onClick={startGoogleAuthFromMobile}
                className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold px-3 py-1.5 rounded-md"
              >
                Sign Up Free
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 relative">
              <span className="text-gray-300 text-xs truncate max-w-[160px]">
                {localStorage.getItem('userEmail')}
              </span>
              <button
                type="button"
                onClick={() => setIsMenuOpen(o => !o)}
                aria-label="Open menu"
                className="p-1.5 rounded-md hover:bg-gray-800 active:bg-gray-700"
              >
                {/* hamburger icon */}
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-gray-300">
                  <line x1="4" y1="6"  x2="20" y2="6"  />
                  <line x1="4" y1="12" x2="20" y2="12" />
                  <line x1="4" y1="18" x2="20" y2="18" />
                </svg>
              </button>

              {isMenuOpen && (
                <>
                  {/* click-outside catcher */}
                  <div className="fixed inset-0 z-40" onClick={() => setIsMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 w-40 bg-gray-900 border border-gray-700 rounded-md shadow-lg z-50">
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-200 hover:bg-gray-800 active:bg-gray-800"
                    >
                      {/* power icon */}
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
                        <line x1="12" y1="2" x2="12" y2="12" />
                      </svg>
                      Log out
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

      </header>

      {/* Scrollable body */}
      <div className="px-3 pb-8 space-y-3">
        {/* Intro video is onboarding chrome — only show to logged-out visitors.
            Signed-in users get straight to data. */}
        {!isLoggedIn && (
          <div className="pt-3">
            {/* Intro video: plays once on landing, then replays briefly and
                freezes ~1s in so we hold on the bull frame instead of the dark
                end frame. muted+playsInline so iOS Safari autoplays. */}
            <video
              ref={introVideoRef}
              src="/alffy-intro-mobile.mp4"
              className="w-full rounded-lg"
              autoPlay
              muted
              playsInline
              preload="auto"
              onEnded={handleIntroVideoEnded}
              onTimeUpdate={(e) => setIntroVideoTime(e.target.currentTime)}
            />

            {/* Caption strip below the video. min-height keeps layout stable
                between captions; the keyed inner span triggers a soft
                fade-in (.lsp-msg) on each caption change. */}
            <div className="mt-2 px-3 min-h-[1.75rem] flex items-center justify-center text-center">
              {currentIntroOverlay && (
                <span
                  key={currentIntroOverlay.start}
                  className="lsp-msg text-white font-medium text-sm sm:text-base leading-tight"
                >
                  {currentIntroOverlay.text}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Single-row horizontally scrollable strip of stock tiles.
            Sits within the body's padding so its left edge lines up with the
            search box below. snap-x gives a tactile feel. */}
        <div className="overflow-x-auto snap-x snap-mandatory no-scrollbar">
          <div className="flex gap-2 w-max">
            {tickers.map(t => {
              const q = quotes.find(x => x.ticker === t) || { ticker: t };
              return (
                <div key={t} className="snap-start shrink-0 w-[170px]">
                  <QuoteCard
                    quote={q}
                    isSelected={ticker?.toUpperCase() === t.toUpperCase()}
                    onSelect={handleCardSelect}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Sticky search bar — always visible while scrolling */}
        <StickyStockSearch selectedCompany={selectedCompany} onSelect={handleSelect} />

        {/* Stock price history */}
        <Card
          title="Stock Price History"
          action={
            <div className="flex items-center gap-0.5 bg-gray-900 rounded p-0.5">
              {['1D', '5D', '1M', '3M', '6M', '1Y', '5Y'].map(r => (
                <button
                  key={r}
                  onClick={() => setTimeRange(r)}
                  className={`px-2 py-0.5 text-[10px] font-semibold rounded ${timeRange === r ? 'bg-blue-600 text-white' : 'text-gray-400'}`}
                >
                  {r}
                </button>
              ))}
            </div>
          }
        >
          {priceHistory.length > 0
            ? <PriceHistoryChart priceHistory={priceHistory} chartType="candle" timeRange={timeRange} />
            : <p className="text-sm text-gray-400 py-8 text-center">Loading…</p>
          }
        </Card>

        {/* Latest market metrics — same fields as desktop StockView */}
        <Card title="Latest Market Metrics">
          <div className="grid grid-cols-3 gap-y-3 gap-x-2">
            <MetricItem label="Open"   value={formatCurrency(companyMetrics?.open)} />
            <MetricItem label="Close"  value={formatCurrency(companyMetrics?.close)} />
            <MetricItem label="High"   value={formatCurrency(companyMetrics?.high)} />
            <MetricItem label="Low"    value={formatCurrency(companyMetrics?.low)} />
            <MetricItem label="Market Cap" value={formatCurrency(companyMetrics?.market_cap, 'B')} />
            <MetricItem label="TTM EPS" value={formatCurrency(companyMetrics?.ttm_eps)} />
            <MetricItem label="P/E"    value={formatNumber(companyMetrics?.pe)} />
            <MetricItem label="P/S"    value={formatNumber(companyMetrics?.price_to_sales)} />
            <MetricItem label="P/B"    value={formatNumber(companyMetrics?.price_to_book)} />
            <MetricItem label="BVPS"   value={formatCurrency(companyMetrics?.bvps)} />
            <MetricItem label="EBT (LTM)" value={formatCurrencyMillions(companyMetrics?.latest_ebt)} />
          </div>
        </Card>

        {/* Revenue / Net Income / EPS quarterly bars */}
        {incomeStatement.length > 0 && (
          <>
            <Card title="Revenue">
              <ConceptChart data={incomeStatement} concept="Revenue" isQuarterly height="280px" maxPeriods={6} />
            </Card>
            <Card title="Net Income Loss">
              <ConceptChart data={incomeStatement} concept="Net Income Loss" isQuarterly height="280px" maxPeriods={6} />
            </Card>
            <Card title="Earnings Per Share Basic">
              <ConceptChart data={incomeStatement} concept="Earnings Per Share Basic" isQuarterly height="280px" maxPeriods={6} />
            </Card>
          </>
        )}

        {/* CAGR — two new candidates side-by-side with the existing line comparison.
            Pick whichever reads best on a phone and we'll keep that one. */}
        {growthMetrics && (
          <>
            <Card title="CAGR Relative">
              <CagrGroupedBar periods={cagrPeriods} series={cagrCompareSeries} />
            </Card>

            {/* CagrComparisonChart provides its own panel chrome, so render bare here. */}
            <CagrComparisonChart periods={cagrPeriods} series={cagrCompareSeries} height="220px" />
          </>
        )}

        {/* Historical P/E + P/B */}
        {valuationTs && (
          <>
            <Card title="Price to Earnings (P/E)">
              <RatioTrendChart
                title="Historical P/E"
                valueLabel="P/E"
                metricKey="pe"
                timeseries={valuationTs.pe?.timeseries}
                range={peRange}
                onRangeChange={setPeRange}
                currentValue={companyMetrics?.pe}
                lineColor="#3b82f6"
                areaColorStart="rgba(59,130,246,0.15)"
                areaColorEnd="rgba(59,130,246,0)"
                currentColorClass="text-blue-400"
              />
            </Card>
            <Card title="Price to Book (P/B)">
              <RatioTrendChart
                title="Historical P/B"
                valueLabel="P/B"
                metricKey="pb"
                timeseries={valuationTs.pb?.timeseries}
                range={pbRange}
                onRangeChange={setPbRange}
                currentValue={companyMetrics?.price_to_book}
                lineColor="#3b82f6"
                areaColorStart="rgba(59,130,246,0.15)"
                areaColorEnd="rgba(59,130,246,0)"
                currentColorClass="text-blue-400"
              />
            </Card>
          </>
        )}

        {/* Financial health — placed just before Income Statement at the bottom */}
        <Card title="Financial Health">
          <div className="grid grid-cols-2 gap-y-3 gap-x-3">
            <MetricItem label="Working Capital"   value={formatCurrencyMillions(financialHealth?.working_capital)} />
            <MetricItem label="Cash & Equivalents" value={formatCurrencyMillions(financialHealth?.cash_and_equivalents)} />
            <MetricItem label="Total Debt"        value={formatCurrencyMillions(financialHealth?.total_debt)} />
            <MetricItem label="Return on Assets"  value={formatPct(financialHealth?.roa)} />
            <MetricItem label="Debt to Equity"   value={formatNumber(financialHealth?.debt_to_equity)} />
            <MetricItem label="Current Ratio"    value={formatNumber(financialHealth?.current_ratio)} />
            <MetricItem label="Quick Ratio"      value={formatNumber(financialHealth?.quick_ratio)} />
            <MetricItem label="Return on Equity" value={formatPct(financialHealth?.roe)} />
          </div>
        </Card>

        {/* Latest Earnings: 4 KPI chips (Revenue & EPS QoQ/YoY) + waterfall income
            statement, mirroring the slides in earnings-shorts. */}
        {incomeStatement.length > 0 && (
          <LatestEarnings
            data={incomeStatement}
            sankeyMapData={sankeyMap}
            isQuarterly={true}
          />
        )}
      </div>
    </div>
  );
}
